<script setup lang="ts">
import { ref, onBeforeUnmount, watch } from 'vue';
import { z } from 'zod';
import {
  FILE_CHUNK_BYTES,
  MAX_FILE_BYTES,
  type FilesConnection,
} from '@atum-remote/protocol/files';
import type { RemoteDevice, RemoteUiAdapter } from '../model.js';
const props = defineProps<{ device: RemoteDevice; adapter?: RemoteUiAdapter | undefined }>();
const entries = ref<{ name: string; directory: boolean; size: number }[]>([]);
const path = ref(''),
  busy = ref(false),
  error = ref(''),
  progress = ref(''),
  truncated = ref(false),
  connected = ref(false);
let connection: FilesConnection | undefined;
let revision = 0;
const listing = z.object({
  entries: z
    .array(
      z.object({
        name: z.string().max(255),
        directory: z.boolean(),
        size: z.number().int().nonnegative(),
      }),
    )
    .max(100),
  truncated: z.boolean(),
});
const joined = (name: string) => (path.value ? `${path.value}/${name}` : name);
async function disconnect() {
  revision++;
  const previous = connection;
  connection = undefined;
  connected.value = false;
  entries.value = [];
  await previous?.close();
}
onBeforeUnmount(() => {
  void disconnect();
});
watch(
  () => props.device.id,
  () => {
    void disconnect();
    path.value = '';
  },
);
async function run(operation: () => Promise<void>) {
  if (busy.value) return;
  busy.value = true;
  error.value = '';
  progress.value = '';
  try {
    await operation();
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'File operation failed.';
  } finally {
    busy.value = false;
    connected.value = connection?.isOpen() === true;
  }
}
async function refresh() {
  if (!connection?.isOpen()) throw new Error('Connect to this device first.');
  const current = revision;
  const result = listing.parse(await connection.request({ operation: 'list', path: path.value }));
  if (revision !== current) return;
  entries.value = result.entries.sort(
    (a, b) => Number(b.directory) - Number(a.directory) || a.name.localeCompare(b.name),
  );
  truncated.value = result.truncated;
}
async function connect() {
  await disconnect();
  const attempt = revision;
  if (!props.adapter?.openFiles) throw new Error('Files are unavailable in this connection.');
  const opened = await props.adapter.openFiles(props.device);
  if (attempt !== revision) {
    await opened.close();
    return;
  }
  connection = opened;
  connected.value = true;
  await refresh();
}
async function download(name: string) {
  const current = connection;
  if (!current?.isOpen()) throw new Error('Connect first.');
  const opened = z
    .object({ transfer: z.uuid(), size: z.number().int().min(0).max(MAX_FILE_BYTES) })
    .parse(await current.request({ operation: 'open_read', path: joined(name) }));
  const chunks: ArrayBuffer[] = [];
  let offset = 0;
  try {
    do {
      const chunk = z
        .object({ data: z.string().max(21848), offset: z.literal(offset), eof: z.boolean() })
        .parse(await current.request({ operation: 'read', transfer: opened.transfer, offset }));
      const data = Uint8Array.from(atob(chunk.data), (char) => char.charCodeAt(0));
      if (
        data.length > FILE_CHUNK_BYTES ||
        offset + data.length > opened.size ||
        (offset + data.length === opened.size && !chunk.eof) ||
        (!data.length && !chunk.eof)
      )
        throw new Error('Invalid file response.');
      chunks.push(data.buffer);
      offset += data.length;
      progress.value = `Downloaded ${offset} of ${opened.size} bytes`;
      if (chunk.eof) {
        if (offset !== opened.size) throw new Error('File changed while downloading.');
        break;
      }
    } while (offset <= opened.size);
    const url = URL.createObjectURL(new Blob(chunks, { type: 'application/octet-stream' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } finally {
    if (current.isOpen())
      await current
        .request({ operation: 'cancel', transfer: opened.transfer })
        .catch(() => undefined);
  }
}
async function upload(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (!file) return;
  await run(async () => {
    const current = connection;
    if (!current?.isOpen() || !props.device.files.controlAvailable)
      throw new Error('Upload access is unavailable.');
    if (file.size > MAX_FILE_BYTES) throw new Error('Choose a file up to 64 MiB.');
    const bytes = new Uint8Array(await file.arrayBuffer());
    const digest = [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))]
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
    const start = z.object({ transfer: z.uuid() }).parse(
      await current.request({
        operation: 'begin_write',
        path: joined(file.name),
        size: file.size,
      }),
    );
    try {
      for (let offset = 0; offset < bytes.length; offset += FILE_CHUNK_BYTES) {
        const chunk = bytes.subarray(offset, offset + FILE_CHUNK_BYTES);
        z.object({ offset: z.literal(offset + chunk.length) }).parse(
          await current.request({
            operation: 'write',
            transfer: start.transfer,
            offset,
            data: btoa(String.fromCharCode(...chunk)),
          }),
        );
        progress.value = `Uploaded ${offset + chunk.length} of ${bytes.length} bytes`;
      }
      z.object({ created: z.literal(true) }).parse(
        await current.request({ operation: 'commit', transfer: start.transfer, digest }),
      );
      progress.value = 'File uploaded.';
      await refresh();
    } finally {
      if (current.isOpen())
        await current
          .request({ operation: 'cancel', transfer: start.transfer })
          .catch(() => undefined);
    }
  });
}
</script>
<template>
  <section class="panel files-panel">
    <h2>Files</h2>
    <p>
      Browse the folder shared from this device. Uploads create new files and keep existing files
      intact.
    </p>
    <button :disabled="busy || !device.files.available" @click="run(connect)">
      {{ connected ? 'Reconnect' : 'Connect to files' }}
    </button>
    <button v-if="connected" @click="disconnect">Disconnect</button>
    <p v-if="!device.files.available">
      Files need a folder selected on the device and a file access grant.
    </p>
    <template v-if="connected">
      <form @submit.prevent="run(refresh)">
        <label
          >Folder<input
            v-model="path"
            :disabled="busy"
            placeholder="Shared folder"
            maxlength="2048" /></label
        ><button :disabled="busy">Open folder</button>
      </form>
      <button
        :disabled="busy || !path"
        @click="
          run(async () => {
            path = path.split('/').slice(0, -1).join('/');
            await refresh();
          })
        "
      >
        Parent folder
      </button>
      <label v-if="device.files.controlAvailable"
        >Upload a file (up to 64 MiB)<input type="file" :disabled="busy" @change="upload"
      /></label>
      <p v-if="truncated">
        This folder has more entries than can be shown. Open a subfolder to narrow the list.
      </p>
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Size</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="entry in entries" :key="entry.name">
            <td>{{ entry.name }}</td>
            <td>{{ entry.directory ? 'Folder' : entry.size + ' bytes' }}</td>
            <td>
              <button
                :disabled="busy"
                @click="
                  run(async () => {
                    if (entry.directory) {
                      path = joined(entry.name);
                      await refresh();
                    } else await download(entry.name);
                  })
                "
              >
                {{ entry.directory ? 'Open' : 'Download' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
    <p v-if="progress" role="status">{{ progress }}</p>
    <p v-if="error" role="alert">{{ error }}</p>
  </section>
</template>
<style scoped>
.files-panel {
  padding: 1.5rem;
  overflow-x: auto;
}
form,
label {
  display: flex;
  gap: 0.6rem;
  align-items: center;
  margin: 1rem 0;
}
table {
  width: 100%;
  text-align: left;
  margin-top: 1rem;
}
td,
th {
  padding: 0.6rem;
}
input {
  min-width: 0;
}
</style>
